import GoogleAIStudioRunDialog from "./GoogleAIStudioRunDialog";
import { useState } from "react";
import { Play, Loader2, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

// Executes a Web Target through the admin endpoint
//   POST /api/gateway/web-targets/{id}/run  { prompt }
// Renders four visual states: idle | running | response | error.
export default function RunWebTargetDialog(props) {
  if (props.target?.provider === "google-ai-studio") return <GoogleAIStudioRunDialog {...props} />;
  return <LegacyRunWebTargetDialog {...props} />;
}
function LegacyRunWebTargetDialog({ target, onClose }) {
  const [task, setTask] = useState("chat");
  const [input, setInput] = useState({});
  const [reading, setReading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState("idle"); // idle | running | response | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const canSend = prompt.trim().length > 0 && status !== "running" && !reading && (task !== "image_edit" || !!input.image_b64);

  const send = async () => {
    setStatus("running");
    setResult(null);
    setError("");
    try {
      const r = await api.post(`/gateway/web-targets/${target.id}/run`, {
        prompt: prompt.trim(), task, input: task === "image_edit" ? input : {},
      });
      // Backend returns 200 OK with ok:false when the upstream VPS fails
      // (so Cloudflare doesn't replace the body with its 520 page). Treat
      // ok:false as the error state and surface the real upstream message.
      if (r.data && r.data.ok === false) {
        const upstream = r.data.upstream_status ? ` (upstream ${r.data.upstream_status})` : "";
        setError(`${r.data.error || "Error del VPS."}${upstream}`);
        setStatus("error");
        return;
      }
      setResult(r.data);
      setStatus("response");
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("error");
    }
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    setInput({});
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 8 * 1024 * 1024) {
      toast.error("Selecciona PNG, JPEG o WebP de hasta 8 MiB.");
      e.target.value = "";
      return;
    }
    setReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setInput({image_b64: String(reader.result).split(",")[1], image_mime_type: file.type});
      setReading(false);
    };
    reader.onerror = () => { setReading(false); toast.error("No se pudo leer la imagen."); };
    reader.readAsDataURL(file);
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(result?.text || "");
      toast.success("Respuesta copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const supportsImages = (target.provider || target.browser_profile || "").toLowerCase() === "gemini" || /^https:\/\/gemini\.google\.com(?:\/|$)/i.test(target.target_url || "");

  const providerLabel =
    target.provider?.trim() || target.browser_profile || "—";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={TID.gateway.webTargetRunDialog}
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-4 w-4 text-emerald-400" />
            Ejecutar «{target.name}»
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-[11px] font-mono text-zinc-400 space-y-0.5">
          <div>Endpoint: <span className="text-zinc-300">{task === "chat" ? "POST /generate" : "POST /generate_image"}</span></div>
          <div>Provider: <span className="text-zinc-300">{providerLabel}</span></div>
          <div>Profile: <span className="text-zinc-300">{target.browser_profile}</span></div>
        </div>

        <label className="text-xs text-zinc-400">
          Tipo de prueba
          <select aria-label="Tipo de prueba" value={task} disabled={status === "running"}
            onChange={(e) => { setTask(e.target.value); setResult(null); setStatus("idle"); }}
            className="block w-full bg-zinc-900 border border-zinc-800 rounded p-2 mt-1">
            <option value="chat">Chat de texto</option>
            {supportsImages && <option value="image_edit">Editar imagen con Gemini</option>}
            {supportsImages && <option value="image_generation">Generar imagen con Gemini</option>}
          </select>
        </label>
        {task === "image_edit" && <label className="text-xs text-zinc-400">
          Fotografía (PNG, JPEG o WebP, hasta 8 MiB)
          <input aria-label="Fotografía" type="file" accept="image/png,image/jpeg,image/webp"
            disabled={status === "running" || reading} onChange={pickImage} className="block mt-2" />
          {input.image_b64 && <img alt="Fotografía a editar" className="max-h-32 mt-2"
            src={`data:${input.image_mime_type};base64,${input.image_b64}`} />}
        </label>}

        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400">Prompt</label>
          <Textarea
            data-testid={TID.gateway.webTargetRunPrompt}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Escribe tu prompt para Gemini…"
            className="bg-zinc-900 border-zinc-800 text-sm resize-none"
            disabled={status === "running"}
          />
        </div>

        <div className="min-h-[120px]">
          {status === "idle" && (
            <div className="rounded-md border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500">
              Escribe un prompt y pulsa <b>Enviar</b>.
            </div>
          )}

          {status === "running" && (
            <div className="flex items-center gap-2 rounded-md border border-blue-900/50 bg-blue-950/20 p-3 text-xs text-blue-200">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Ejecutando Playwright en el VPS… puede tardar unos segundos.
            </div>
          )}

          {status === "response" && result && (
            <div
              data-testid={TID.gateway.webTargetRunResult}
              className="rounded-md border border-emerald-900/50 bg-emerald-950/10 p-3 space-y-2"
            >
              <div className="flex items-center justify-between text-[11px] text-emerald-300/80">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Respuesta recibida
                  {typeof result.duration_ms === "number" && (
                    <span className="ml-2 text-zinc-500 font-mono">
                      {result.duration_ms} ms
                    </span>
                  )}
                </div>
                <button
                  onClick={copyText}
                  className="text-zinc-400 hover:text-white flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" /> Copiar
                </button>
              </div>
              <div className="text-sm text-zinc-200 whitespace-pre-wrap font-mono break-words">
                {result.text}
              </div>
              {(result.artifacts || []).filter(a => a.type === "image").map((a, i) => (
                <div key={i}>
                  <img alt={`Resultado ${i + 1}`} className="max-h-96 w-full object-contain"
                    src={`data:${a.mime_type};base64,${a.data_b64}`} />
                  <a download={`gemini-${i + 1}.${a.mime_type.split("/")[1]}`}
                    href={`data:${a.mime_type};base64,${a.data_b64}`}>Descargar imagen</a>
                </div>
              ))}
            </div>
          )}

          {status === "error" && (
            <div
              data-testid={TID.gateway.webTargetRunError}
              className="rounded-md border border-red-900/50 bg-red-950/10 p-3 text-xs text-red-300 flex items-start gap-2"
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="whitespace-pre-wrap break-words">{error}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            data-testid={TID.gateway.webTargetRunSend}
            onClick={send}
            disabled={!canSend}
            size="sm"
            className="gap-2 bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50"
          >
            {status === "running" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            {status === "running" ? "Ejecutando…" : "Enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
